/**
 * popupCapture — detect the top-most open popup/modal on the page so the
 * "Report Issue" capture can screenshot ONLY the popup (not the whole page).
 *
 * The app has no single modal primitive — it mixes custom Tailwind overlays,
 * the shared ModalWrapper, MUI Dialog/Drawer/Popover and Leaflet popups — so
 * detection is heuristic:
 *   1. Prefer semantic popup nodes ([role=dialog], MUI *-paper, .leaflet-popup).
 *   2. Otherwise, the top-most full-viewport fixed overlay's content panel.
 *
 * Elements inside a `data-html2canvas-ignore="true"` subtree are never returned
 * (that covers our own header widgets, the floating icon, and the global New
 * Ticket modal), so the capture target is always the *other* popup on screen.
 *
 * The returned element is engine-agnostic — it is fed to modern-screenshot's
 * `domToPng` in `screenshotStore.ts` (the `data-html2canvas-ignore` name is kept
 * for consistency with the capture filter, not because html2canvas is used).
 */

/** Visible = rendered, non-trivial size, not display:none / hidden / transparent. */
function isVisible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < 40 || r.height < 40) return false;
  const s = getComputedStyle(el);
  return s.display !== 'none' && s.visibility !== 'hidden' && parseFloat(s.opacity || '1') > 0.05;
}

/** True when the element (or an ancestor) is explicitly excluded from capture. */
function isIgnored(el: HTMLElement): boolean {
  return !!el.closest('[data-html2canvas-ignore="true"]');
}

/** Highest z-index found on the element or any of its ancestors (stacking hint). */
function stackZ(el: HTMLElement): number {
  let z = 0;
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    const v = parseInt(getComputedStyle(node).zIndex || '', 10);
    if (!Number.isNaN(v)) z = Math.max(z, v);
    node = node.parentElement;
  }
  return z;
}

/** Pick the visually top-most element: highest stacking z, then later in DOM. */
function topMost(els: HTMLElement[]): HTMLElement {
  return els.reduce((best, el) => {
    if (!best) return el;
    const dz = stackZ(el) - stackZ(best);
    if (dz > 0) return el;
    if (dz < 0) return best;
    return best.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING ? el : best;
  }, els[0]);
}

/** A background colour that is a real, opaque-ish panel fill (not a dark scrim). */
function isPanelBackground(bg: string): boolean {
  if (!bg || bg === 'transparent') return false;
  if (bg.startsWith('rgba(0, 0, 0')) return false;          // dark backdrop scrim
  const m = bg.match(/rgba?\([^)]*?,\s*([\d.]+)\)\s*$/);    // rgba(...,0) fully transparent
  if (m && parseFloat(m[1]) === 0) return false;
  return true;
}

/** Full-viewport fixed overlays that look like a modal container. */
function fullscreenOverlays(): HTMLElement[] {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const out: HTMLElement[] = [];
  document.body.querySelectorAll<HTMLElement>('*').forEach((el) => {
    const s = getComputedStyle(el);
    if (s.position !== 'fixed') return;
    if (isIgnored(el) || !isVisible(el)) return;
    const z = parseInt(s.zIndex || '', 10);
    if (Number.isNaN(z) || z < 20) return;
    const r = el.getBoundingClientRect();
    if (r.width >= vw * 0.85 && r.height >= vh * 0.85 && r.top <= vh * 0.15 && r.left <= vw * 0.15) {
      out.push(el);
    }
  });
  return out;
}

/** From an overlay, return its content panel (the card), never the dark scrim. */
function panelOf(overlay: HTMLElement): HTMLElement {
  const semantic = overlay.querySelector<HTMLElement>('[role="dialog"], .MuiDialog-paper, .MuiDrawer-paper');
  if (semantic && isVisible(semantic) && !isIgnored(semantic)) return semantic;

  let best: HTMLElement | null = null;
  let bestArea = 0;
  overlay.querySelectorAll<HTMLElement>('*').forEach((el) => {
    if (!isVisible(el) || isIgnored(el)) return;
    if (!isPanelBackground(getComputedStyle(el).backgroundColor)) return;
    const r = el.getBoundingClientRect();
    const area = r.width * r.height;
    if (area > bestArea) {
      bestArea = area;
      best = el;
    }
  });
  return best || overlay;
}

/**
 * Returns the popup/modal element to screenshot, or null when none is open.
 */
export function findOpenPopup(): HTMLElement | null {
  const semantic = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[role="dialog"], .MuiDialog-paper, .MuiDrawer-paper, .MuiPopover-paper, .leaflet-popup'
    )
  ).filter((el) => isVisible(el) && !isIgnored(el));
  if (semantic.length) return topMost(semantic);

  const overlays = fullscreenOverlays();
  if (!overlays.length) return null;
  return panelOf(topMost(overlays));
}
